'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Search, Menu, X, Film, Tv, Clock, Tag, Home, LogIn, UserCircle } from 'lucide-react';
import GlobalSearch from '@/components/GlobalSearch';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';


const NAV_LINKS = [
  { label: 'Home', href: '/', icon: Home },
  { label: 'Movies', href: '/movies-page', icon: Film },
  { label: 'Series', href: '/series', icon: Tv },
  { label: 'Latest', href: '/latest', icon: Clock },
  { label: 'Genres', href: '/genres', icon: Tag },
];

export default function Navbar() {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'glass-card border-b border-border' : 'bg-transparent'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2 flex-shrink-0">
              <AppLogo size={32} />
              <span className="font-sans font-800 text-xl tracking-tight text-gradient-cyan hidden sm:block">
                NeonCine
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {NAV_LINKS.map((link) => (
                <Link
                  key={`nav-${link.href}`}
                  href={link.href}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 relative ${
                    isActive(link.href)
                      ? 'nav-active text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-2">
              {/* Search toggle */}
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-white/5 transition-all duration-200"
                aria-label="Toggle search"
              >
                {searchOpen ? <X size={20} /> : <Search size={20} />}
              </button>

              {/* Auth-aware button */}
              {authLoading ? (
                <div className="hidden sm:block w-16 h-7 rounded-lg bg-white/5 animate-pulse" />
              ) : user ? (
                  <Link
                    href="/profile"
                    className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg glass-card border border-border hover:border-primary/40 hover:text-primary text-xs font-semibold text-muted-foreground transition-all"
                  >
                    <UserCircle size={14} />
                    Profile
                  </Link>
                ) : (
                  <Link
                    href="/sign-up-login-screen"
                    className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg neon-btn-cyan text-xs font-semibold"
                  >
                    <LogIn size={14} />
                    Sign In
                  </Link>
                )}

              {/* Mobile menu */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-white/5 transition-all"
                aria-label="Toggle menu"
              >
                {menuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>

          {/* Search bar */}
          {searchOpen && (
            <div className="pb-3 animate-fade-in">
              <GlobalSearch onClose={() => setSearchOpen(false)} />
            </div>
          )}
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="lg:hidden glass-card border-t border-border animate-fade-in">
            <div className="px-4 py-4 space-y-1">
              {NAV_LINKS.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={`mobile-nav-${link.href}`}
                    href={link.href}
                    onClick={() => setMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
                      isActive(link.href)
                        ? 'bg-primary/10 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                    }`}
                  >
                    <Icon size={18} />
                    {link.label}
                  </Link>
                );
              })}
              {authLoading ? (
                <div className="w-full h-10 rounded-xl bg-white/5 animate-pulse mt-2" />
              ) : user ? (
                  <Link
                    href="/profile"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold glass-card border border-border text-muted-foreground hover:text-primary mt-2"
                  >
                    <UserCircle size={18} />
                    My Profile
                  </Link>
                ) : (
                  <Link
                    href="/sign-up-login-screen"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold neon-btn-cyan mt-2"
                  >
                    <LogIn size={18} />
                    Sign In
                  </Link>
                )}
            </div>
          </div>
        )}
      </nav>

      {/* Bottom mobile nav */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 glass-card border-t border-border safe-area-pb">
        <div className="flex items-center justify-around px-2 py-2">
          {NAV_LINKS.map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={`bottom-nav-${link.href}`}
                href={link.href}
                className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-all duration-200 ${
                  isActive(link.href)
                    ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon size={20} className={isActive(link.href) ? 'drop-shadow-[0_0_6px_var(--neon-cyan)]' : ''} />
                <span className="text-[10px] font-medium">{link.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
}